import type { Modality } from '../contracts';
import type { Outcome } from '../progression';
import { ROUND_CLOCKS, roundKind } from '../rounds';
import { roundOutcome, type BacktestRound } from './backtest';
import { customStrategySchema, lookbackOf, matchRule, RUSH_GOLD, rushSide, sidesOf, type CustomRule, type CustomStrategyDef, type PatternToken, type Side } from './custom';

/**
 * Strategy finder: replays a catalogue of single-rule strategies over past rounds
 * and ranks them. Same semantics as backtestStrategy with a custom strategy of one
 * rule and gales on the same side (decision reads the rounds before it, the entry
 * settles on that round, each gale on the next one), only faster, so a few hundred
 * candidates run in the browser on every filter change. Past ≠ future.
 */

export type ScanFamily = 'NUMBER' | 'PATTERN' | 'STREAK' | 'ALTERNATION' | 'BREAK' | 'FREQUENCY';

export const SCAN_FAMILY_LABEL: Record<ScanFamily, string> = {
  NUMBER: 'Número',
  PATTERN: 'Padrão',
  STREAK: 'Sequência',
  ALTERNATION: 'Alternância',
  BREAK: 'Quebra',
  FREQUENCY: 'Frequência',
};

export function scanFamilies(modality: Modality): ScanFamily[] {
  const all: ScanFamily[] = ['NUMBER', 'PATTERN', 'STREAK', 'ALTERNATION', 'BREAK', 'FREQUENCY'];
  return modality === 'EVEN_ODD' ? all : all.filter((f) => f !== 'NUMBER');
}

/**
 * Rush rounds as finder rounds: the rules read each round by its candle color (verde UP / cinza DOWN),
 * and an entry on it won when it flew at least `cashoutTicks` (the exit the user wants). A Rush rule
 * only says WHEN to enter: every match is an entry, each gale goes into the next round.
 */
export function rushFinderRounds(rounds: readonly { id: number; crashedAt: number; ticks: number }[], cashoutTicks: number): BacktestRound[] {
  return rounds.map((r) => ({ id: r.id, endAt: r.crashedAt, digit: 0, move: rushSide(r.ticks) === 'RISE' ? 'UP' : 'DOWN', hit: r.ticks >= cashoutTicks, ticks: r.ticks }));
}

export interface ScanCandidate {
  family: ScanFamily;
  rule: CustomRule;
}

/** Every rule the finder tries, simplest first (ties keep the simplest). */
export function scanCandidates(modality: Modality, families: readonly ScanFamily[] = scanFamilies(modality), maxSkip = 2): ScanCandidate[] {
  const [a, b] = sidesOf(modality);
  const rush = modality === 'ACCUMULATOR';
  // On the Rush every match is a bet: one entry variant per trigger is enough (the others trade the same rounds).
  const entries = <T,>(options: readonly T[]): readonly T[] => (rush ? options.slice(0, 1) : options);
  const out: ScanCandidate[] = [];
  let n = 0;
  const add = (family: ScanFamily, rule: Record<string, unknown>) => out.push({ family, rule: { id: `s${++n}`, enabled: true, ...rule } as CustomRule });
  const want = new Set(families);

  if (want.has('NUMBER') && modality === 'EVEN_ODD') {
    for (let skip = 0; skip <= maxSkip; skip++) {
      for (let d = 0; d <= 9; d++) {
        const sequence: PatternToken[] = [d, ...Array<PatternToken>(skip).fill('ANY')];
        for (const entry of [a, b]) add('NUMBER', { type: 'PATTERN', sequence, entry });
      }
    }
    for (const count of [2, 3]) for (const entry of ['AGAINST', 'WITH']) add('NUMBER', { type: 'REPEAT', count, entry });
  }
  if (want.has('STREAK')) {
    for (let length = 2; length <= 6; length++) {
      for (const exact of [true, false]) for (const entry of entries(['AGAINST', 'WITH'])) add('STREAK', { type: 'STREAK', length, exact, entry });
    }
  }
  if (want.has('ALTERNATION')) for (let length = 3; length <= 6; length++) for (const entry of entries(['BREAK', 'CONTINUE'])) add('ALTERNATION', { type: 'ALTERNATION', length, entry });
  if (want.has('BREAK')) for (let minRun = 2; minRun <= 5; minRun++) for (const entry of entries(['FOLLOW', 'RETURN'])) add('BREAK', { type: 'BREAK', minRun, entry });
  if (want.has('FREQUENCY')) {
    for (const [window, min] of [[10, 7], [10, 8], [20, 13], [20, 14]] as const) for (const entry of entries(['MINORITY', 'MAJORITY'])) add('FREQUENCY', { type: 'FREQUENCY', window, min, entry });
  }
  if (want.has('PATTERN') && rush) {
    // Rush: the three history candles — cinza (até 99%), verde (100%+) and dourada (1.000%+).
    const candles: PatternToken[] = [b, a, RUSH_GOLD];
    for (let len = 2; len <= 5; len++) {
      for (let code = 0; code < 3 ** len; code++) {
        const sequence = Array.from({ length: len }, (_, i) => candles[Math.floor(code / 3 ** (len - 1 - i)) % 3]!);
        add('PATTERN', { type: 'PATTERN', sequence, entry: a });
      }
    }
  } else if (want.has('PATTERN')) {
    for (let len = 2; len <= 5; len++) {
      for (let mask = 0; mask < 1 << len; mask++) {
        const sequence = Array.from({ length: len }, (_, i) => ((mask >> (len - 1 - i)) & 1 ? b : a));
        for (const entry of rush ? [a] : [a, b]) add('PATTERN', { type: 'PATTERN', sequence, entry });
      }
    }
  }
  return out;
}

export interface ScanOptions {
  modality: Modality;
  /** Gales after the first entry (0 = single entry). */
  gales: number;
  /** Stake multiplier of each gale. */
  multiplier: number;
  /** Net return of a win, e.g. 0.85. */
  payoutRate: number;
  families?: readonly ScanFamily[];
  /** Rounds a NUMBER trigger may wait before entering (0–3). */
  maxSkip?: number;
  /** Cycles a strategy needs in the period to be ranked. */
  minChains?: number;
  /**
   * Side of each gale, as the robot's "Lado do gale": SAME repeats the entry (default), FOLLOW goes on the side that
   * came out (after an Empate the side stays), INVERT switches side on every gale, Empates included.
   */
  galeSide?: GaleSide;
}

export type ChainMark = 'W' | 'L';
/** The gale sides a test can replay (SIGNAL and a plan need the robot's own reading). */
export type GaleSide = 'SAME' | 'INVERT' | 'FOLLOW';

export interface ScanResult {
  /** Stable for the same rule (survives re-scans). */
  key: string;
  family: ScanFamily;
  rule: CustomRule;
  /** Side the rule enters (fixed sides; null when it depends on the rounds). */
  entrySide: Side | null;
  chains: number;
  chainWins: number;
  chainLosses: number;
  /** Cycles won at each level: [first entry, gale 1, …]. */
  winsByLevel: number[];
  /** 0–1, per cycle (entry + its gales). */
  hitRate: number;
  /** Entries sent (first entries + gales). */
  entries: number;
  /** Deepest gale any cycle needed (0 = always settled on the first entry). */
  maxLevel: number;
  /** Result for a first entry of 1 (USD), gales multiplied. */
  net: number;
  /** Result of the second half of the period only. */
  recentNet: number;
  maxDrawdown: number;
  maxLossStreak: number;
  /** Cycles per hour of open market (see cycleMinutes). */
  perHour: number;
  /**
   * Minutes of open market per cycle ("1 ciclo a cada 4,2 min"): rounds × the game's clock, as the admin
   * counts it, so Polarium's pauses don't stretch it. Null with no cycle.
   */
  cycleMinutes: number | null;
  /** Running result, at most ~48 points. */
  curve: number[];
  /** Newest last, at most 12. */
  recent: ChainMark[];
  /** Closing epoch of the last cycle. */
  lastAt: number | null;
}

/** The flame shown next to an assertividade: 95%+ on fire, 90%+ hot, nothing below. */
export type Heat = 'FIRE' | 'HOT' | null;

export function heatOf(hitRate: number): Heat {
  return hitRate >= 0.95 ? 'FIRE' : hitRate >= 0.9 ? 'HOT' : null;
}

/**
 * Cycles a strategy needs in the period to be ranked, so a lucky 3 of 3 never tops the list:
 * ~1 per 40 rounds, between 8 (1h) and 40 (12h or more).
 */
export function minChainsFor(rounds: number): number {
  return Math.max(8, Math.min(40, Math.round(rounds / 40)));
}

const cents = (v: number) => Math.round(v * 100) / 100;

/** Empate (a level close, or no reading): no side, and on Dígitos no number either — as windowSides/windowDigits read it live. */
const isTie = (modality: Modality, r: BacktestRound) => modality !== 'ACCUMULATOR' && (r.move === 'FLAT' || r.move === null);

function sideOfRound(modality: Modality, r: BacktestRound): Side | null {
  if (modality === 'EVEN_ODD') return isTie(modality, r) ? null : r.digit % 2 === 0 ? 'EVEN' : 'ODD';
  return r.move === 'UP' ? 'RISE' : r.move === 'DOWN' ? 'FALL' : null;
}

function downsample(values: number[], max = 48): number[] {
  if (values.length <= max) return values;
  const step = (values.length - 1) / (max - 1);
  return Array.from({ length: max }, (_, i) => values[Math.round(i * step)]!);
}

/** Fixed entry side of a rule, when it has one (the Rush always bets). */
function fixedSide(rule: CustomRule, modality: Modality): Side | null {
  if (modality === 'ACCUMULATOR') return 'RISE';
  return rule.type === 'PATTERN' || rule.type === 'DIGIT' ? rule.entry : null;
}

export function ruleKey(rule: CustomRule): string {
  const { id: _id, enabled: _enabled, ...rest } = rule;
  return JSON.stringify(rest);
}

/** Replays one rule. Exposed for tests; the UI calls scanStrategies. */
export function replayRule(rule: CustomRule, rounds: readonly BacktestRound[], opts: Omit<ScanOptions, 'families' | 'maxSkip' | 'minChains'>, prepared?: Prepared) {
  const p = prepared ?? prepare(opts.modality, rounds);
  const { modality, gales, multiplier, payoutRate } = opts;
  const N = rounds.length;
  const lb = lookbackOf({ rules: [rule] } as CustomStrategyDef);
  const half = rounds[0] ? (rounds[0].endAt + rounds[N - 1]!.endAt) / 2 : 0;
  const stakes = Array.from({ length: gales + 1 }, (_, k) => cents(multiplier ** k));
  const winsByLevel = Array<number>(gales + 1).fill(0);
  const marks: ChainMark[] = [];
  const curve = [0];
  let chains = 0;
  let chainLosses = 0;
  let entries = 0;
  let maxLevel = 0;
  let net = 0;
  let recentNet = 0;
  let peak = 0;
  let maxDrawdown = 0;
  let lossStreak = 0;
  let maxLossStreak = 0;
  let lastAt: number | null = null;
  let sig = 0;

  let i = 1;
  while (i < N) {
    const from = p.start(i);
    // Shared per-round windows (built once for every rule); longer windows read the same as the live one.
    const matched =
      lb <= MAX_SHARED_LOOKBACK
        ? matchRule(rule, modality, p.windowSides(i), p.windowDigits(i), p.windowFlights(i))
        : matchRule(rule, modality, p.revSides.slice(from, from + lb), p.revDigits.slice(from, from + lb), p.revFlights.slice(from, from + lb));
    // Rush: a match means "bet on the next round" (won when it flies to the cash-out), like the robot.
    const side = matched && modality === 'ACCUMULATOR' ? 'RISE' : matched;
    if (!side) {
      i++;
      continue;
    }
    sig = (Math.imul(sig, 31) + i * 2 + (side === p.a ? 0 : 1)) | 0;
    let level = 0;
    let chainNet = 0;
    let won = false;
    let closed = false;
    let refunded = false;
    let bet = side;
    const switching = opts.galeSide !== undefined && opts.galeSide !== 'SAME' && modality !== 'ACCUMULATOR';
    while (i < N) {
      const out = modality === 'ACCUMULATOR' ? (rounds[i]!.hit === true ? 'WIN' : 'LOSS') : (bet === p.a ? p.paidA : p.paidB)[i]!;
      i++;
      entries++;
      // Equal close (or no reading): Polarium returns the stake. A gale keeps its level for the next round;
      // a first entry that came back is no cycle, and the robot reads the market again (as it does live).
      if (out === 'NEUTRAL') {
        if (level === 0) {
          refunded = true;
          break;
        }
        if (switching && opts.galeSide === 'INVERT') bet = bet === p.a ? p.b : p.a;
        continue;
      }
      if (switching && out === 'LOSS') bet = bet === p.a ? p.b : p.a;
      maxLevel = Math.max(maxLevel, level);
      if (out === 'WIN') {
        chainNet += stakes[level]! * payoutRate;
        winsByLevel[level]!++;
        won = true;
        closed = true;
        break;
      }
      chainNet -= stakes[level]!;
      if (level >= gales) {
        closed = true;
        break;
      }
      level++;
    }
    if (refunded) continue;
    if (!closed) break; // period ended mid-cycle: leave it out
    chains++;
    chainNet = cents(chainNet);
    net = cents(net + chainNet);
    const at = rounds[i - 1]!.endAt;
    if (at > half) recentNet = cents(recentNet + chainNet);
    lastAt = at;
    peak = Math.max(peak, net);
    maxDrawdown = Math.max(maxDrawdown, cents(peak - net));
    curve.push(net);
    marks.push(won ? 'W' : 'L');
    if (won) lossStreak = 0;
    else {
      chainLosses++;
      lossStreak++;
      maxLossStreak = Math.max(maxLossStreak, lossStreak);
    }
  }

  const chainWins = chains - chainLosses;
  const hitRate = chains ? chainWins / chains : 0;

  return {
    chains,
    chainWins,
    chainLosses,
    winsByLevel,
    hitRate,
    entries,
    maxLevel,
    net,
    recentNet,
    maxDrawdown,
    maxLossStreak,
    perHour: p.minutes > 0 ? chains / (p.minutes / 60) : 0,
    cycleMinutes: chains && p.minutes > 0 ? p.minutes / chains : null,
    curve: downsample(curve),
    recent: marks.slice(-12),
    lastAt,
    signature: `${chains}:${sig}`,
  };
}

/** Longest look-back among the finder's rules (Frequência 20) with margin. */
const MAX_SHARED_LOOKBACK = 24;

interface Prepared {
  a: Side;
  b: Side;
  /** Rounds with a side before round i, newest first (Empates skipped, as ruleWindow reads them live), at most MAX_SHARED_LOOKBACK (cached). */
  windowSides: (i: number) => (Side | null)[];
  windowDigits: (i: number) => number[];
  /** Rush: ticks flown by those rounds (empty for the other modalities). */
  windowFlights: (i: number) => number[];
  /** What an entry on each round paid, on the modality's first / second side (see roundOutcome). */
  paidA: Outcome[];
  paidB: Outcome[];
  /** Rounds with a side, newest first; round i's window starts at rev*[start(i)]. */
  revSides: Side[];
  revDigits: number[];
  revFlights: number[];
  start: (i: number) => number;
  /** Minutes of open market the rounds cover (the Rush has no fixed clock: first to last round). */
  minutes: number;
}

function prepare(modality: Modality, rounds: readonly BacktestRound[]): Prepared {
  const sides = rounds.map((r) => sideOfRound(modality, r));
  const [a, b] = sidesOf(modality);
  const first = rounds[0];
  const last = rounds[rounds.length - 1];
  const N = rounds.length;
  // The rules never see an Empate: only rounds with a side, in order. sidedBefore[i] = how many come before round i.
  const kept = rounds.filter((_, i) => sides[i] !== null);
  const M = kept.length;
  const sidedBefore = new Array<number>(N + 1);
  sidedBefore[0] = 0;
  for (let i = 0; i < N; i++) sidedBefore[i + 1] = sidedBefore[i]! + (sides[i] !== null ? 1 : 0);
  const start = (i: number) => M - sidedBefore[i]!;
  const revSides = sides.filter((x): x is Side => x !== null).reverse();
  const revDigits = kept.map((r) => r.digit).reverse();
  const revFlights = rounds.every((r) => r.ticks !== undefined) ? kept.map((r) => r.ticks!).reverse() : [];
  const ws: Side[][] = new Array(N);
  const wd: number[][] = new Array(N);
  const wf: number[][] = new Array(N);
  return {
    a,
    b,
    windowSides: (i) => (ws[i] ??= revSides.slice(start(i), start(i) + MAX_SHARED_LOOKBACK)),
    windowDigits: (i) => (wd[i] ??= revDigits.slice(start(i), start(i) + MAX_SHARED_LOOKBACK)),
    windowFlights: (i) => (revFlights.length ? (wf[i] ??= revFlights.slice(start(i), start(i) + MAX_SHARED_LOOKBACK)) : revFlights),
    paidA: rounds.map((r) => roundOutcome(modality, a, r)),
    paidB: rounds.map((r) => roundOutcome(modality, b, r)),
    revSides,
    revDigits,
    revFlights,
    start,
    minutes:
      modality === 'ACCUMULATOR'
        ? first && last
          ? (last.endAt - first.endAt) / 60
          : 0
        : (N * ROUND_CLOCKS[roundKind(modality)].cycleSeconds) / 60,
  };
}

/** Ranks every candidate, most assertive first. Rules that trade the exact same rounds count once. */
export function scanStrategies(rounds: readonly BacktestRound[], opts: ScanOptions): ScanResult[] {
  const p = prepare(opts.modality, rounds);
  const minChains = opts.minChains ?? 8;
  const seen = new Set<string>();
  const out: ScanResult[] = [];
  for (const c of scanCandidates(opts.modality, opts.families, opts.maxSkip)) {
    const r = replayRule(c.rule, rounds, opts, p);
    if (r.chains < minChains || seen.has(r.signature)) continue;
    seen.add(r.signature);
    const { signature: _s, ...stats } = r;
    out.push({ key: ruleKey(c.rule), family: c.family, rule: c.rule, entrySide: fixedSide(c.rule, opts.modality), ...stats });
  }
  // Most assertive first; with the same %, the one more cycles back up.
  return out.sort((x, y) => y.hitRate - x.hitRate || y.chains - x.chains || y.net - x.net);
}

/**
 * A robot definition with just this rule; gales go on the side the test replayed (repeat the entry unless
 * told otherwise). A Rush robot also takes the growth and cash-out the finder replayed with.
 */
export function strategyFromScan(rule: CustomRule, accu?: { growthRate: number; cashoutTicks: number }, galeSide: GaleSide = 'SAME'): CustomStrategyDef {
  return customStrategySchema.parse({ rules: [{ ...rule, id: 'r1', enabled: true }], gale: { direction: galeSide }, ...(accu ? { accu } : {}) });
}
